import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import ExcelJS from 'exceljs';
import JSZip from 'jszip';

import { getSessionIdToken } from '../auth/apiAuth';
import { SERVICE_COLORS } from '../utility/theme';

import axios from 'axios';

export default function LabelsRender({ auth, selectedProject, templates, loadingTemplates, onSnackbar, deactivation }) {
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [csvFile, setCsvFile] = useState(null);
    const [csvData, setCsvData] = useState({ headers: [], rows: [] });
    const [dataModified, setDataModified] = useState(false);
    const [confirmResetOpen, setConfirmResetOpen] = useState(false);
    const [options, setOptions] = useState({});
    const [rendering, setRendering] = useState(false);
    const [renderResult, setRenderResult] = useState(null);
    const [renderErrors, setRenderErrors] = useState(null);

    const fileInputRef = useRef(null);
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const labelsColor = SERVICE_COLORS.labels.main;

    useEffect(() => {
        setSelectedTemplate(null);
        setCsvFile(null);
        setCsvData({ headers: [], rows: [] });
        setOptions({});
        setRenderResult(null);
        setRenderErrors(null);
    }, [selectedProject]);

    useEffect(() => {
        if (selectedTemplate?.paginationOptions?.length > 0) {
            const defaults = {};
            selectedTemplate.paginationOptions.forEach(opt => {
                defaults[opt.nome] = opt.value !== undefined ? opt.value : opt.default;
            });
            setOptions(defaults);
        } else {
            setOptions({});
        }
    }, [selectedTemplate]);

    const handleSelectTemplate = (tmpl) => {
        setSelectedTemplate(tmpl);
        setRenderResult(null);
        setRenderErrors(null);
    };

    const handleChangeTemplate = () => {
        setSelectedTemplate(null);
        setCsvFile(null);
        setCsvData({ headers: [], rows: [] });
        setRenderResult(null);
        setRenderErrors(null);
    };

    const [fileError, setFileError] = useState(null);

    const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx'];

    const parseCsvText = (text) => {
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length === 0) return null;

        const separator = lines[0].includes(';') ? ';' : ',';

        const parseCsvLine = (line, sep) => {
            const cells = [];
            let current = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (inQuotes) {
                    if (ch === '"') {
                        if (i + 1 < line.length && line[i + 1] === '"') {
                            current += '"';
                            i++;
                        } else {
                            inQuotes = false;
                        }
                    } else {
                        current += ch;
                    }
                } else {
                    if (ch === '"') {
                        inQuotes = true;
                    } else if (ch === sep) {
                        cells.push(current.trim());
                        current = '';
                    } else {
                        current += ch;
                    }
                }
            }
            cells.push(current.trim());
            return cells;
        };

        const headers = parseCsvLine(lines[0], separator);
        const rows = lines.slice(1).map(line => {
            const cells = parseCsvLine(line, separator);
            const row = {};
            headers.forEach((h, i) => { row[h] = cells[i] || ''; });
            return row;
        });
        return { headers, rows };
    };

    const HEADING_LOCALE_MAP = {
        'Fogli di lavoro': 'it-IT',
        'Arbeitsblätter': 'de-DE',
        'Feuilles de calcul': 'fr-FR',
        'Hojas de cálculo': 'es-ES',
        'Werkbladen': 'nl-NL',
        'Planilhas': 'pt-BR',
        'Arkusze': 'pl-PL',
        'Листы': 'ru-RU',
        'Regneark': 'da-DK',
        'Kalkylblad': 'sv-SE',
        'Laskentataulukot': 'fi-FI',
        'Çalışma Sayfaları': 'tr-TR',
        'Φύλλα εργασίας': 'el-GR',
        'Listy': 'cs-CZ',
        'Munkalapok': 'hu-HU',
        'Foi de lucru': 'ro-RO',
        'Delovni listi': 'sl-SI',
        'Radni listovi': 'hr-HR',
        'Worksheets': 'en-US',
        'Werkblätter': 'de-CH',
        'Folhas de cálculo': 'pt-PT',
    };

    const STYLE_LOCALE_MAP = {
        'Normale': 'it-IT',
        'Standard': 'de-DE',
        'Standaard': 'nl-NL',
        'Normalny': 'pl-PL',
        'Обычный': 'ru-RU',
        'Κανονικό': 'el-GR',
        'Normál': 'hu-HU',
    };

    const extractLocaleFromXlsx = async (buffer) => {
        try {
            const zip = await JSZip.loadAsync(buffer);

            // Strategy 1: DataMashup Culture (most reliable)
            const customXml = zip.file('customXml/item1.xml');
            if (customXml) {
                const raw = await customXml.async('arraybuffer');
                const text = new TextDecoder().decode(raw).replace(/\0/g, '');
                const base64Match = text.match(/[A-Za-z0-9+/=]{100,}/);
                if (base64Match) {
                    const decoded = Uint8Array.from(atob(base64Match[0]), c => c.charCodeAt(0));
                    let offset = 0;
                    while (offset < decoded.length - 4) {
                        const len = decoded[offset] | (decoded[offset+1] << 8) | (decoded[offset+2] << 16) | (decoded[offset+3] << 24);
                        offset += 4;
                        if (len <= 0 || len > decoded.length - offset) break;
                        const section = decoded.slice(offset, offset + len);
                        offset += len;
                        if (section[0] === 0x50 && section[1] === 0x4B) {
                            const innerZip = await JSZip.loadAsync(section);
                            const pkgXml = await innerZip.file('Config/Package.xml')?.async('string');
                            if (pkgXml) {
                                const m = pkgXml.match(/<Culture>([^<]+)<\/Culture>/);
                                if (m) return m[1];
                            }
                        }
                    }
                }
            }

            // Strategy 2: app.xml HeadingPairs
            const appXml = await zip.file('docProps/app.xml')?.async('string');
            if (appXml) {
                for (const [heading, locale] of Object.entries(HEADING_LOCALE_MAP)) {
                    if (appXml.includes(heading)) return locale;
                }
            }

            // Strategy 3: styles.xml cellStyle name
            const stylesXml = await zip.file('xl/styles.xml')?.async('string');
            if (stylesXml) {
                const m = stylesXml.match(/cellStyle name="([^"]+)"/);
                if (m && STYLE_LOCALE_MAP[m[1]]) return STYLE_LOCALE_MAP[m[1]];
            }
        } catch { /* extraction failed */ }
        return null;
    };

    const [numericLocale, setNumericLocale] = useState('en-US');
    const [hasNumericCells, setHasNumericCells] = useState(false);


    const parseExcelFile = async (file) => {
        const buffer = await file.arrayBuffer();

        const detectedLocale = await extractLocaleFromXlsx(buffer);
        const locale = detectedLocale || navigator.language || 'en-US';
        setNumericLocale(locale);

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        if (!worksheet || worksheet.rowCount === 0) return null;

        const headers = [];
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            headers[colNumber - 1] = cell.text?.trim() || `Column${colNumber}`;
        });
        if (headers.length === 0) return null;

        let foundNumeric = false;
        const rows = [];
        const formatCell = (cell) => {
            if (cell.value == null) return '';
            if (typeof cell.value === 'number') {
                foundNumeric = true;
                const str = cell.value.toString();
                const dot = str.indexOf('.');
                const decimals = dot !== -1 ? str.length - dot - 1 : 0;
                return cell.value.toLocaleString(locale, {
                    useGrouping: false,
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                });
            }
            if (cell.value instanceof Date) {
                return cell.value.toLocaleDateString(locale);
            }
            return cell.text?.trim() || '';
        };

        for (let i = 2; i <= worksheet.rowCount; i++) {
            const wsRow = worksheet.getRow(i);
            const row = {};
            let hasData = false;
            headers.forEach((h, idx) => {
                const cell = wsRow.getCell(idx + 1);
                const value = formatCell(cell);
                row[h] = value;
                if (value) hasData = true;
            });
            if (hasData) rows.push(row);
        }
        setHasNumericCells(foundNumeric);
        return { headers, rows };
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setFileError(null);
        setRenderResult(null);
        setRenderErrors(null);

        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
            setCsvFile(file);
            setCsvData({ headers: [], rows: [] });
            setFileError(file.name);
            return;
        }

        setCsvFile(file);

        try {
            if (ext === '.xlsx') {
                const result = await parseExcelFile(file);
                if (!result) {
                    setFileError(file.name);
                    return;
                }
                setCsvData(result);
            } else {
                const text = await file.text();
                const result = parseCsvText(text);
                if (!result) {
                    setFileError(file.name);
                    return;
                }
                setCsvData(result);
            }
        } catch {
            setFileError(file.name);
        }
    };

    const handleRemoveFile = () => {
        setCsvFile(null);
        setCsvData({ headers: [], rows: [] });
        setFileError(null);
        setDataModified(false);
        setHasNumericCells(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleOptionChange = (name, value) => {
        setOptions(prev => ({ ...prev, [name]: value }));
    };

    const handleRender = async () => {
        if (!csvFile || !selectedProject || !selectedTemplate) return;
        setRendering(true);
        setRenderResult(null);
        setRenderErrors(null);

        try {
            const token = await getSessionIdToken();

            const formData = new FormData();
            // Rebuild CSV from edited data
            const csvContent = [
                csvData.headers.join(','),
                ...csvData.rows.map(row => csvData.headers.map(h => {
                    const val = (row[h] ?? '').toString();
                    return val.includes(',') || val.includes('"') || val.includes('\n')
                        ? `"${val.replace(/"/g, '""')}"` : val;
                }).join(','))
            ].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            formData.append('file', blob, csvFile.name);
            formData.append('project_id', selectedTemplate.project_id || selectedProject);
            if (Object.keys(options).length > 0) {
                formData.append('options', JSON.stringify(options));
            }

            const { data } = await axios.post(`${API_BASE}/labels/render`, formData, {
                headers: { Authorization: token }
            });

            if (data.status === 'validation_error') {
                setRenderErrors(data.errors || []);
            } else {
                setRenderResult(data);
                onSnackbar(`PDF generated: ${data.labels_count} labels in ${((data.total_time_ms || data.render_time_ms) / 1000).toFixed(1)}s`, 'success');
            }
        } catch (err) {
            const errData = err.response?.data;
            const code = errData?.code || errData?.error_code || (err.response ? `HTTP_${err.response.status}` : 'NETWORK_ERROR');
            const message = errData?.message || errData?.error || 'An error occurred during job processing.';
            setRenderErrors([{ message, code }]);
        } finally {
            setRendering(false);
        }
    };

    const handleDownload = async () => {
        if (!renderResult?.pdf_url) return;
        try {
            const res = await fetch(renderResult.pdf_url);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `labels-${selectedProject?.replace('/', '-')}-${renderResult.render_id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            window.open(renderResult.pdf_url, '_blank');
        }
    };

    const handleReset = () => {
        setSelectedTemplate(null);
        setCsvFile(null);
        setCsvData({ headers: [], rows: [] });
        setOptions({});
        setRenderResult(null);
        setRenderErrors(null);
        setDataModified(false);
        setConfirmResetOpen(false);
    };

    const handleStartOver = () => {
        if (csvFile && dataModified) {
            setConfirmResetOpen(true);
        } else {
            handleReset();
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const canGenerate = csvFile && selectedTemplate && !rendering && !deactivation && !fileError;

    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%' }}>
            {/* Left panel */}
            <Box sx={{ width: { xs: '100%', md: '30%' }, minWidth: { md: 320 }, p: 3, overflow: 'auto', borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}>

                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                    <Typography variant="h5" color={labelsColor} fontWeight={600}>
                        Launch Labels
                    </Typography>
                    {selectedTemplate && (
                        <Link component="button" variant="body2" onClick={handleStartOver} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <RestartAltIcon fontSize="small" /> Start over
                        </Link>
                    )}
                </Stack>

                {deactivation && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <b>{deactivation.disableLaunch || 'This project has been deactivated. Please contact sales@pagination.com'}</b>
                    </Alert>
                )}

                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                    {/* Template selection */}
                    {!selectedTemplate && (
                        <Box sx={{ maxHeight: '90vh', overflow: 'auto' }}>
                            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Template
                            </Typography>
                            {loadingTemplates ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress size={32} />
                                </Box>
                            ) : (!templates || templates.length === 0) ? (
                                <Typography variant="body2" color="text.secondary">
                                    No templates available for this project.
                                </Typography>
                            ) : (
                                <Grid container spacing={2} justifyContent="center">
                                    {templates.map((tmpl) => (
                                        <Grid item key={tmpl.project_id || tmpl.name} sx={{ width: 180 }}>
                                            <Tooltip
                                                arrow
                                                placement="top"
                                                title={
                                                    <Box sx={{ p: 0.5 }}>
                                                        <Typography variant="subtitle2" fontWeight={700}>{tmpl.name}</Typography>
                                                        {tmpl.project_id && (
                                                            <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                                                                {tmpl.project_id}
                                                            </Typography>
                                                        )}
                                                        {tmpl.label_size && (
                                                            <Typography variant="caption" display="block">
                                                                {tmpl.label_size.width_mm}×{tmpl.label_size.height_mm} mm
                                                                {tmpl.grid ? ` · ${tmpl.grid.cols}×${tmpl.grid.rows}/pag` : ''}
                                                            </Typography>
                                                        )}
                                                        {tmpl.placeholders?.length > 0 && (
                                                            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.85 }}>
                                                                Fields: {tmpl.placeholders.join(', ')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            >
                                                <Card
                                                    variant="outlined"
                                                    sx={{ cursor: 'pointer', width: '100%', height: 140, '&:hover': { borderColor: labelsColor } }}
                                                >
                                                    <CardActionArea onClick={() => handleSelectTemplate(tmpl)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                                                        {tmpl.thumbnail_url ? (
                                                            <Box sx={{ height: 100, position: 'relative', bgcolor: 'grey.50', flexShrink: 0 }}>
                                                                <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    <CircularProgress size={20} sx={{ color: 'grey.400' }} />
                                                                </Box>
                                                                <Box
                                                                    component="img"
                                                                    src={tmpl.thumbnail_url}
                                                                    alt={tmpl.name}
                                                                    onLoad={(e) => { e.target.previousSibling.style.display = 'none'; }}
                                                                    onError={(e) => { e.target.style.display = 'none'; e.target.previousSibling.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'contain', pt: 1, position: 'relative' }}
                                                                />
                                                                <Box sx={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                                                                    <AutoAwesomeMosaicIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                                                                </Box>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', flexShrink: 0 }}>
                                                                <AutoAwesomeMosaicIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                                                            </Box>
                                                        )}
                                                        <CardContent sx={{ py: 0.5, px: 1, overflow: 'hidden', flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                                                            <Typography variant="caption" noWrap title={tmpl.name} sx={{ width: '100%', textAlign: 'center' }}>
                                                                {tmpl.name}
                                                            </Typography>
                                                        </CardContent>
                                                    </CardActionArea>
                                                </Card>
                                            </Tooltip>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    )}

                    {/* Selected template full info */}
                    {selectedTemplate && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Template
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Box sx={{ flexShrink: 0, width: 120, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50', alignSelf: 'flex-start' }}>
                                    {selectedTemplate.thumbnail_url ? (
                                        <Box sx={{ position: 'relative', width: '100%', minHeight: 120 }}>
                                            <Box
                                                component="img"
                                                src={selectedTemplate.thumbnail_url}
                                                alt={selectedTemplate.name}
                                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                                sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
                                            />
                                            <Box sx={{ display: 'none', position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
                                                <AutoAwesomeMosaicIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <Box sx={{ minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <AutoAwesomeMosaicIcon sx={{ fontSize: 40, color: 'grey.400' }} />
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                                        Name: {selectedTemplate.name}
                                    </Typography>
                                    {selectedTemplate.project_id && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                            <strong>Project:</strong> {selectedTemplate.project_id}
                                        </Typography>
                                    )}
                                    {selectedTemplate.label_size && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                            <strong>Label size:</strong> {selectedTemplate.label_size.width_mm}×{selectedTemplate.label_size.height_mm} mm
                                        </Typography>
                                    )}
                                    {selectedTemplate.grid && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                            <strong>Grid:</strong> {selectedTemplate.grid.cols} columns × {selectedTemplate.grid.rows} rows
                                        </Typography>
                                    )}
                                    {selectedTemplate.page_size && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                            <strong>Page size:</strong> {selectedTemplate.page_size.width_mm}×{selectedTemplate.page_size.height_mm} mm
                                        </Typography>
                                    )}
                                    {selectedTemplate.barcode_fields?.length > 0 && (
                                        <Typography variant="body2" sx={{ mb: 0.3 }}>
                                            <strong>Barcode format:</strong> {(selectedTemplate.barcode_format || 'code128').toUpperCase()}
                                        </Typography>
                                    )}
                                    {selectedTemplate.placeholders?.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                                <strong>Required fields:</strong>
                                            </Typography>
                                            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                                {selectedTemplate.placeholders.map(p => {
                                                    const isImage = selectedTemplate.image_fields?.includes(p);
                                                    const isQr = selectedTemplate.qr_fields?.includes(p);
                                                    const isBarcode = selectedTemplate.barcode_fields?.includes(p);
                                                    let color = 'default';
                                                    let icon = <TextFieldsIcon />;
                                                    if (isImage) { color = 'secondary'; icon = <ImageIcon />; }
                                                    else if (isQr) { color = 'primary'; icon = <QrCode2Icon />; }
                                                    else if (isBarcode) { color = 'primary'; icon = <ViewWeekIcon />; }
                                                    return (
                                                        <Chip key={p} label={p} size="small" variant="outlined" color={color} icon={icon} />
                                                    );
                                                })}
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    {/* Template options */}
                    {selectedTemplate?.paginationOptions?.length > 0 && (
                        <Box
                            component="fieldset"
                            sx={{
                                mb: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                pt: 1.5,
                                m: 0,
                            }}
                        >
                            <Typography component="legend" variant="overline" color="text.secondary" sx={{ px: 0.5 }}>
                                Template Options
                            </Typography>
                            <Stack spacing={2.5}>
                                {selectedTemplate.paginationOptions.map(opt => {
                                    if (opt.component === 'checkBox') {
                                        return (
                                            <FormControlLabel
                                                key={opt.nome}
                                                control={
                                                    <Checkbox
                                                        checked={!!options[opt.nome]}
                                                        onChange={(e) => handleOptionChange(opt.nome, e.target.checked)}
                                                    />
                                                }
                                                label={<Typography variant="body2">{opt.nome}</Typography>}
                                            />
                                        );
                                    }
                                    if (opt.component === 'form') {
                                        return (
                                            <Box key={opt.nome}>
                                                <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                                                    {opt.nome}
                                                </Typography>
                                                <TextField
                                                    value={options[opt.nome] || ''}
                                                    onChange={(e) => handleOptionChange(opt.nome, e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    variant="outlined"
                                                />
                                            </Box>
                                        );
                                    }
                                    if (opt.component === 'select' && opt.options) {
                                        return (
                                            <Box key={opt.nome}>
                                                <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                                                    {opt.nome}
                                                </Typography>
                                                <Select
                                                    value={options[opt.nome] || ''}
                                                    onChange={(e) => handleOptionChange(opt.nome, e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    variant="outlined"
                                                >
                                                    {opt.options.map(o => (
                                                        <MenuItem key={o} value={o}>{o}</MenuItem>
                                                    ))}
                                                </Select>
                                            </Box>
                                        );
                                    }
                                    return (
                                        <Box key={opt.nome}>
                                            <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 0.5 }}>
                                                {opt.nome}
                                            </Typography>
                                            <TextField
                                                value={options[opt.nome] || ''}
                                                onChange={(e) => handleOptionChange(opt.nome, e.target.value)}
                                                size="small"
                                                fullWidth
                                                variant="outlined"
                                            />
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    {/* Data upload */}
                    {selectedTemplate && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                Data
                            </Typography>
                            {!csvFile ? (
                                <Box
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = labelsColor; e.currentTarget.style.backgroundColor = 'rgba(51,151,226,0.05)'; }}
                                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = ''; e.currentTarget.style.backgroundColor = ''; }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.currentTarget.style.borderColor = '';
                                        e.currentTarget.style.backgroundColor = '';
                                        const file = e.dataTransfer.files?.[0];
                                        if (file) handleFileChange({ target: { files: [file] } });
                                    }}
                                    sx={{
                                        py: 4,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        border: '2px dashed',
                                        borderColor: 'grey.300',
                                        borderRadius: 1,
                                        bgcolor: 'grey.50',
                                        transition: 'border-color 0.2s, background-color 0.2s',
                                        '&:hover': { borderColor: labelsColor, bgcolor: 'action.hover' },
                                    }}
                                >
                                    <UploadFileIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Drag & drop or click to upload CSV or Excel (.xlsx)
                                    </Typography>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,.xlsx"
                                        hidden
                                        onChange={handleFileChange}
                                    />
                                </Box>
                            ) : (
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <UploadFileIcon color="primary" fontSize="small" />
                                        <Typography variant="body2">{csvFile.name}</Typography>
                                    </Stack>
                                    <IconButton size="small" onClick={handleRemoveFile}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            )}
                        </Box>
                    )}
                </Paper>

            </Box>

            {/* Right panel - CSV data preview */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, bgcolor: 'grey.100', overflow: 'auto', minHeight: { xs: '50vh', md: 'auto' }, borderTop: { xs: '2px solid', md: 'none' }, borderColor: { xs: 'divider', md: 'transparent' } }}>
                {fileError ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Stack alignItems="center" spacing={1.5}>
                            <Box sx={{ width: 64, height: 64, bgcolor: 'rgba(211,47,47,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <UploadFileIcon sx={{ color: 'error.main', fontSize: 32, opacity: 1 }} />
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Unreadable file
                            </Typography>
                            <Typography variant="body2" color="text.secondary" textAlign="center">
                                The file <strong>{fileError}</strong> cannot be processed.<br />
                                Please upload a valid CSV or Excel (.xlsx) file with at least one header row.
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<UploadFileIcon />}
                                onClick={() => { handleRemoveFile(); setTimeout(() => fileInputRef.current?.click(), 100); }}
                            >
                                Upload another file
                            </Button>
                        </Stack>
                    </Box>
                ) : csvData.headers.length > 0 ? (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {csvFile?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {csvData.rows.length} rows · {csvData.headers.length} columns
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                — click a cell to edit
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <Tooltip title="Download CSV">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        const content = [
                                            csvData.headers.join(','),
                                            ...csvData.rows.map(row => csvData.headers.map(h => {
                                                const val = (row[h] ?? '').toString();
                                                return val.includes(',') || val.includes('"') || val.includes('\n')
                                                    ? `"${val.replace(/"/g, '""')}"` : val;
                                            }).join(','))
                                        ].join('\n');
                                        const blob = new Blob([content], { type: 'text/csv' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = csvFile?.name || 'data.csv';
                                        a.click();
                                        URL.revokeObjectURL(url);
                                    }}
                                >
                                    <DownloadIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        {selectedTemplate?.placeholders?.length > 0 && (() => {
                            const missing = selectedTemplate.placeholders.filter(p => !csvData.headers.includes(p));
                            return missing.length > 0 ? (
                                <Alert severity="warning" sx={{ mb: 1 }}>
                                    Template fields not found in CSV: <strong>{missing.join(', ')}</strong>
                                </Alert>
                            ) : null;
                        })()}
                        {hasNumericCells && (
                            <Alert severity="info" sx={{ mb: 1 }}>
                                Numeric values were converted from Excel format. Please verify that decimal and thousands separators are correct — click any cell to edit if needed.
                                <br />If many values need fixing, save your Excel as CSV (which preserves your local formatting) and upload the CSV instead.
                            </Alert>
                        )}
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {csvData.headers.map(h => (
                                            <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {csvData.rows.map((row, i) => (
                                        <TableRow key={i} hover>
                                            {csvData.headers.map(h => (
                                                <TableCell
                                                    key={h}
                                                    contentEditable
                                                    suppressContentEditableWarning
                                                    onBlur={(e) => {
                                                        const newRows = [...csvData.rows];
                                                        newRows[i] = { ...newRows[i], [h]: e.target.textContent };
                                                        setCsvData(prev => ({ ...prev, rows: newRows }));
                                                        setDataModified(true);
                                                    }}
                                                    sx={{ whiteSpace: 'nowrap', cursor: 'text', '&:focus': { outline: `2px solid ${labelsColor}`, outlineOffset: -2 } }}
                                                >
                                                    {row[h]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Stack alignItems="center" spacing={1}>
                            <Box sx={{ width: 60, height: 60, bgcolor: 'grey.300', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <UploadFileIcon sx={{ color: 'grey.500', fontSize: 30 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                Select a template and upload data to get started
                            </Typography>
                        </Stack>
                    </Box>
                )}

                {/* Generate button */}
                {selectedTemplate && csvFile && (
                    <Box sx={{ p: 2, pt: 0, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={rendering ? null : <FlashOnIcon />}
                            onClick={handleRender}
                            disabled={!canGenerate}
                            sx={{
                                py: 1.5,
                                px: 4,
                                fontWeight: 700,
                                fontSize: '0.95rem',
                                textTransform: 'uppercase',
                                bgcolor: labelsColor,
                                '&:hover': { bgcolor: SERVICE_COLORS.labels.dark },
                            }}
                        >
                            {rendering ? <CircularProgress size={24} color="inherit" /> : 'Generate Labels'}
                        </Button>
                    </Box>
                )}

                {/* Render error */}
                {renderErrors && renderErrors.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
                        <Stack alignItems="center" spacing={1.5}>
                            <Box sx={{ width: 64, height: 64, bgcolor: 'rgba(211,47,47,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Box sx={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid', borderColor: 'error.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="body1" color="error" fontWeight={700}>!</Typography>
                                </Box>
                            </Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Generation failed
                            </Typography>
                            <Typography variant="body2" textAlign="center">
                                {renderErrors[0]?.message || 'An error occurred during job processing.'}
                            </Typography>
                            {renderErrors[0]?.code && (
                                <Typography variant="caption" color="text.secondary" sx={{ bgcolor: 'grey.100', px: 1, py: 0.5, borderRadius: 0.5 }}>
                                    Error code: <code style={{ fontWeight: 600 }}>{renderErrors[0].code}</code>
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<RestartAltIcon />}
                                    onClick={handleRender}
                                    sx={{ bgcolor: labelsColor, '&:hover': { bgcolor: SERVICE_COLORS.labels.dark } }}
                                >
                                    Retry
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleStartOver}
                                >
                                    Start over
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                )}

                {/* Result - PDF preview */}
                {renderResult && (
                    <Box sx={{ p: 2, pt: 0 }}>
                        <Alert
                            severity="success"
                            sx={{ mb: 2 }}
                            action={
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                        size="small"
                                        color="inherit"
                                        startIcon={<OpenInNewIcon />}
                                        href={renderResult.pdf_url}
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        Open
                                    </Button>
                                    <Button
                                        size="small"
                                        color="inherit"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownload}
                                    >
                                        Download
                                    </Button>
                                </Stack>
                            }
                        >
                            PDF generated: {renderResult.labels_count} labels, {renderResult.pages} pages ({((renderResult.total_time_ms || renderResult.render_time_ms) / 1000).toFixed(1)}s, {formatBytes(renderResult.pdf_size_bytes)})
                        </Alert>
                        {renderResult.pdf_url && (
                            <Box
                                component="iframe"
                                src={renderResult.pdf_url}
                                title="PDF preview"
                                sx={{ width: '100%', height: 700, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                            />
                        )}
                    </Box>
                )}
            </Box>

            {/* Confirm reset dialog */}
            <Dialog open={confirmResetOpen} onClose={() => setConfirmResetOpen(false)}>
                <DialogTitle>Start over</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Any changes made to the data will be lost. If you have edited the CSV, download it first using the download button in the data panel.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleReset} variant="contained" autoFocus>
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
